import { EditOutlined } from '@mui/icons-material'
import { Box, IconButton, InputBase, Stack, Tooltip, Typography } from '@mui/material'
import { ChangeEvent, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUpdateRequestProperties } from '../../helpers/useUpdateRequestProperties'

type EditableRequestTitleProps = {
  requestTitle: string
  requestId: string
  disabled: boolean
}

export const EditableRequestTitle = ({ requestTitle, requestId, disabled }: EditableRequestTitleProps) => {
  const { t } = useTranslation('shared')
  const { mutateAsync: updateRequestProperties } = useUpdateRequestProperties()
  const [inputValue, setInputValue] = useState(requestTitle)
  const inputRef = useRef<HTMLInputElement>(null)
  const escapePressedRef = useRef(false)

  const startEditing = () => {
    setInputValue(requestTitle)
    inputRef.current?.focus()
    inputRef.current?.select()
  }

  const handleSave = async () => {
    if (escapePressedRef.current) {
      escapePressedRef.current = false
      return
    }

    const trimmed = inputValue.trim()

    if (!trimmed || trimmed === requestTitle) {
      setInputValue(requestTitle)
      return
    }

    await updateRequestProperties({ requestId, name: trimmed })
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      sx={{
        minWidth: 0,
        '& .edit-title-button': { opacity: 0 },
        '&:hover .edit-title-button, &:focus-within .edit-title-button': { opacity: 1 },
      }}
      data-testid="editable-title-container"
    >
      <Box
        sx={{
          position: 'relative',
          minWidth: 0,
          width: { xs: '100%', md: 'auto' },
          '&:focus-within': { flex: 1 },
        }}
      >
        <Typography
          variant="display-h1"
          component="h2"
          fontWeight={600}
          aria-hidden
          sx={{
            visibility: 'hidden',
            whiteSpace: { md: 'nowrap' },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {inputValue}
        </Typography>
        <InputBase
          inputRef={inputRef}
          value={inputValue}
          readOnly={disabled}
          inputProps={{ tabIndex: disabled ? -1 : 0 }}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={(e) => {
            if (e.key === 'Enter') inputRef.current?.blur()
            if (e.key === 'Escape') {
              escapePressedRef.current = true
              setInputValue(requestTitle)
              inputRef.current?.blur()
            }
          }}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            typography: 'display-h1',
            fontWeight: 600,
            color: 'text.default',
            pointerEvents: 'none',
            '&:focus-within': { pointerEvents: 'auto' },
            '& .MuiInputBase-input': {
              p: 0,
              cursor: 'default',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: { xs: 'center', md: 'left' },
            },
            '&:focus-within .MuiInputBase-input': {
              cursor: 'text',
              overflow: 'visible',
              textOverflow: 'clip',
            },
          }}
          data-testid="request-title-input"
        />
      </Box>
      {!disabled && (
        <Tooltip title={t('table-filters.actions.rename')}>
          <IconButton
            className="edit-title-button"
            sx={{ height: 28, width: 28, display: { xs: 'none', md: 'inline-flex' } }}
            onClick={startEditing}
            aria-label={t('table-filters.actions.rename')}
            data-testid="edit-title-button"
          >
            <EditOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  )
}
